import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import { announce as sbAnnounce } from '../utils/soundbox';

// Wave 47 · every money/order notification the backend now emits gets a spoken alert.
const VOICE_KINDS = {
  // Marketplace (unchanged, retained from support fix 2026-09-24)
  marketplace_order: (n) => ['new_order', { amount: n.meta?.amount_credited }],
  marketplace_order_admin: (n) => ['new_order', { amount: n.meta?.amount }],
  marketplace_order_status_seller: (n) => ['order_' + (n.meta?.status || 'update'), {}],
  marketplace_order_status: (n) => ['order_' + (n.meta?.status || 'update'), {}],
  // ATC escrow / OTP-verify settlements
  atc_payment_released: (n) => ['payment_received', { amount: n.meta?.amount }],
  atc_escrow_confirmed: (n) => ['escrow_confirmed', { amount: n.meta?.amount }],
  // AT deposit funnel
  at_deposit_request: (n) => ['at_deposit_request', { amount: n.meta?.amount }],
  at_deposit_approved: (n) => ['at_deposit_approved', { amount: n.meta?.at_credited || n.meta?.amount }],
  at_deposit_rejected: (n) => ['at_deposit_rejected', { amount: n.meta?.amount }],
};

const NotificationContext = createContext();

// Default notification sound (pleasant chime)
const DEFAULT_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [settings, setSettings] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [showSoundPrompt, setShowSoundPrompt] = useState(false);
  const audioRef = useRef(null);
  const previousUnreadRef = useRef(0);
  const isFirstLoadRef = useRef(true);
  const seenIdsRef = useRef(new Set());

  // Fetch settings for custom sound URL
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings');
        setSettings(res.data);
      } catch (e) {
        console.error('Failed to fetch settings:', e);
      }
    };
    fetchSettings();
  }, []);

  // Check if sound was previously enabled
  useEffect(() => {
    const enabled = localStorage.getItem('notificationSoundEnabled') === 'true';
    setSoundEnabled(enabled);
  }, []);

  // Play notification sound for 5 seconds
  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) {
      console.log('Sound not enabled yet');
      return;
    }

    const soundUrl = settings?.notification_sound_url || DEFAULT_SOUND_URL;
    
    // Stop any existing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }

    try {
      const audio = new Audio(soundUrl);
      audio.loop = true;
      audio.volume = 0.6;
      audioRef.current = audio;

      audio.play()
        .then(() => {
          console.log('Notification sound playing');
          // Stop after 5 seconds
          setTimeout(() => {
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
              audioRef.current = null;
            }
          }, 5000);
        })
        .catch(e => {
          console.log('Audio play failed:', e.message);
          // Show prompt to enable sound
          setShowSoundPrompt(true);
        });
    } catch (e) {
      console.error('Audio creation failed:', e);
    }
  }, [settings, soundEnabled]);

  // Enable sound (must be called from user interaction)
  const enableSound = useCallback(() => {
    const soundUrl = settings?.notification_sound_url || DEFAULT_SOUND_URL;
    const audio = new Audio(soundUrl);
    audio.volume = 0.6;
    
    audio.play()
      .then(() => {
        setTimeout(() => audio.pause(), 1000); // Play for 1 second as confirmation
        setSoundEnabled(true);
        localStorage.setItem('notificationSoundEnabled', 'true');
        setShowSoundPrompt(false);
        console.log('Sound enabled');
      })
      .catch(e => {
        console.error('Failed to enable sound:', e);
      });
  }, [settings]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count')
      ]);
      
      const newNotifications = notifRes.data;
      const newUnreadCount = countRes.data.count;
      
      // Show sound prompt only ONCE per session if:
      // 1. First load
      // 2. Has unread notifications
      // 3. Sound not enabled
      // 4. User hasn't dismissed it before (check localStorage)
      const hasUserDismissed = localStorage.getItem('sound_prompt_dismissed') === 'true';
      if (isFirstLoadRef.current && newUnreadCount > 0 && !soundEnabled && !hasUserDismissed) {
        setShowSoundPrompt(true);
      }
      
      // Support fix 2026-09-24: speak marketplace order alerts (one after another, never dropped)
      const fresh = newNotifications.filter(n => !n.read && !seenIdsRef.current.has(n.id));
      const voiceFresh = fresh.filter(n => VOICE_KINDS[n.kind] && n.play_sound !== false);
      const onlyVoice = fresh.length > 0 && voiceFresh.length === fresh.length;
      if (!isFirstLoadRef.current) {
        voiceFresh.slice(0, 5).reverse().forEach((n, i) => {
          setTimeout(() => {
            try { const [key, data] = VOICE_KINDS[n.kind](n); sbAnnounce(key, data); } catch (_) {}
          }, i * 4000);
        });
      }
      newNotifications.forEach(n => seenIdsRef.current.add(n.id));

      // Play sound when new notifications arrive (after initial load)
      // Skip if all newly-arrived notifications explicitly have play_sound: false (per-vendor mute)
      if (!isFirstLoadRef.current && newUnreadCount > previousUnreadRef.current) {
        const arrived = newNotifications.filter(n => !n.read).slice(0, newUnreadCount - previousUnreadRef.current);
        const anySoundAllowed = arrived.length === 0 || arrived.some(n => n.play_sound !== false);
        if (anySoundAllowed && !onlyVoice) playNotificationSound();
      }
      
      // Check if any new notification has play_sound flag
      setNotifications(prevNotifications => {
        const hasNewPlaySound = newNotifications.some(
          n => !n.read && n.play_sound && !prevNotifications.find(old => old.id === n.id)
        );
        if (hasNewPlaySound && !isFirstLoadRef.current && !onlyVoice) {
          playNotificationSound();
        }
        return newNotifications;
      });
      
      // Play sound on first load if enabled and has unread with play_sound flag
      if (isFirstLoadRef.current && newNotifications.some(n => !n.read && n.play_sound) && soundEnabled) {
        setTimeout(() => playNotificationSound(), 500);
      }
      
      isFirstLoadRef.current = false;
      previousUnreadRef.current = newUnreadCount;
      setUnreadCount(newUnreadCount);
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    }
  }, [user, playNotificationSound, soundEnabled]);

  // Reset refs when user changes (login/logout)
  useEffect(() => {
    if (user) {
      isFirstLoadRef.current = true;
      previousUnreadRef.current = 0;
      seenIdsRef.current = new Set();
    }
  }, [user?.id]);

  // Poll for new notifications every 10 seconds
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      previousUnreadRef.current = Math.max(0, previousUnreadRef.current - 1);
    } catch (e) {
      console.error('Failed to mark as read:', e);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      previousUnreadRef.current = 0;
    } catch (e) {
      console.error('Failed to mark all as read:', e);
    }
  };

  // Manual trigger for testing
  const triggerSound = () => {
    if (soundEnabled) {
      playNotificationSound();
    } else {
      enableSound();
    }
  };

  const value = {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    playNotificationSound,
    triggerSound,
    soundEnabled,
    enableSound,
    showSoundPrompt,
    setShowSoundPrompt,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
